/**
 * NotificationToast - Rich toast notification for server notifications
 * Shows title, message, and allows click to navigate
 */
import React, { useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Platform,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
import { getTextAlign } from '../utils/rtl';
export interface ServerNotification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  created_at: string;
  is_read: boolean;
  link?: string;
  sender_name?: string;
  department_name?: string;
  related_object_type?: string;
  related_object_id?: number;
}

interface NotificationToastProps {
  visible: boolean;
  notification: ServerNotification | null;
  duration?: number;
  onHide: () => void;
  onPress?: () => void;
}

// Icons
const BellIcon = ({ size = 24, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const CheckCircleIcon = ({ size = 24, color = tokens.colors.raw.green["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M9 12L11 14L15 10"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const XCircleIcon = ({ size = 24, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M15 9L9 15M9 9L15 15"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const AlertCircleIcon = ({ size = 24, color = '#F59E0B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 8V12"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 16H12.01"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const TargetIcon = ({ size = 24, color = tokens.colors.raw.blue["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const CloseIcon = ({ size = 18, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M18 6L6 18M6 6L18 18"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const getNotificationConfig = (type: string, isDark: boolean) => {
  switch (type) {
    case 'goal_approved':
    case 'initiative_approved':
    case 'change_approved':
      return {
        bgColor: isDark ? '#064E3B' : '#ECFDF5',
        borderColor: tokens.colors.raw.green["500"],
        iconBgColor: isDark ? '#065F46' : '#D1FAE5',
        Icon: CheckCircleIcon,
        iconColor: tokens.colors.raw.green["500"],
      };
    case 'goal_rejected':
    case 'initiative_rejected':
    case 'change_rejected':
    case 'goal_deleted':
      return {
        bgColor: isDark ? '#7F1D1D' : tokens.colors.raw.red["50"],
        borderColor: tokens.colors.raw.red["500"],
        iconBgColor: isDark ? '#991B1B' : '#FEE2E2',
        Icon: XCircleIcon,
        iconColor: tokens.colors.raw.red["500"],
      };
    case 'goal_submitted':
    case 'initiative_submitted':
    case 'change_request':
      return {
        bgColor: isDark ? '#78350F' : '#FFFBEB',
        borderColor: '#F59E0B',
        iconBgColor: isDark ? '#92400E' : tokens.colors.raw.amber["100"],
        Icon: AlertCircleIcon,
        iconColor: '#F59E0B',
      };
    case 'goal_assigned':
    case 'initiative_assigned':
    case 'goal_edited':
      return {
        bgColor: isDark ? '#1E3A5F' : '#EFF6FF',
        borderColor: tokens.colors.raw.blue["500"],
        iconBgColor: isDark ? '#1E40AF' : '#DBEAFE',
        Icon: TargetIcon,
        iconColor: tokens.colors.raw.blue["500"],
      };
    case 'ticket_update':
      return {
        bgColor: isDark ? '#1E3A5F' : '#EFF6FF',
        borderColor: tokens.colors.raw.blue["500"],
        iconBgColor: isDark ? '#1E40AF' : '#DBEAFE',
        Icon: TargetIcon,
        iconColor: tokens.colors.raw.blue["500"],
      };
    default:
      return {
        bgColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.white,
        borderColor: tokens.colors.semantic.general["Main Color"],
        iconBgColor: isDark ? '#4B1C1C' : tokens.colors.raw.red["50"],
        Icon: BellIcon,
        iconColor: tokens.colors.semantic.general["Main Color"],
      };
  }
};

export default function NotificationToast({
  visible,
  notification,
  duration = 5000,
  onHide,
  onPress,
}: NotificationToastProps) {
  const { colors, isDark } = useTheme();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-100)).current;

  // Theme colors for text
  const themeColors = {
    titleColor: isDark ? tokens.colors.raw.gray["50"] : tokens.colors.raw.gray["900"],
    messageColor: isDark ? tokens.colors.raw.gray["300"] : tokens.colors.raw.gray["500"],
    closeIconColor: isDark ? tokens.colors.raw.gray["400"] : tokens.colors.raw.gray["400"],
  };

  // useNativeDriver is not supported on web
  const useNativeDriver = Platform.OS !== 'web';

  useEffect(() => {
    if (visible && notification) {
      // Show animation
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver,
        }),
        Animated.spring(translateY, {
          toValue: 0,
          friction: 8,
          tension: 40,
          useNativeDriver,
        }),
      ]).start();

      // Auto hide after duration
      const timer = setTimeout(() => {
        hideToast();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [visible, notification]);

  const hideToast = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver,
      }),
      Animated.timing(translateY, {
        toValue: -100,
        duration: 200,
        useNativeDriver,
      }),
    ]).start(() => {
      onHide();
    });
  };

  if (!visible || !notification) return null;

  const config = getNotificationConfig(notification.notification_type, isDark);
  const IconComponent = config.Icon;

  return (
    <Animated.View
      style={[
        styles.container,
        {
          backgroundColor: config.bgColor,
          borderStartColor: config.borderColor,
          opacity: fadeAnim,
          transform: [{ translateY }],
        },
      ]}
    >
      <TouchableOpacity
        style={styles.content}
        onPress={() => {
          hideToast();
          if (onPress) onPress();
        }}
        activeOpacity={0.9}
      >
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: config.iconBgColor }]}>
          <IconComponent size={24} color={config.iconColor} />
        </View>

        {/* Text Content */}
        <View style={styles.textContainer}>
          <Text style={[styles.title, { color: themeColors.titleColor }]} numberOfLines={1}>
            {notification.title}
          </Text>
          <Text style={[styles.message, { color: themeColors.messageColor }]} numberOfLines={2}>
            {notification.message}
          </Text>
          {notification.department_name && (
            <View style={styles.orgBadge}>
              <Text style={styles.orgText}>{notification.department_name}</Text>
            </View>
          )}
        </View>

        {/* Close Button */}
        <IconButton
          variant="ghost"
          size="sm"
          icon={<CloseIcon color={themeColors.closeIconColor} />}
          accessibilityLabel="Close"
          onPress={(e) => {
            e.stopPropagation();
            hideToast();
          }}
          style={{ marginHorizontal: tokens.spacing.absolute["2"] }}
        />
      </TouchableOpacity>
    </Animated.View>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    position: 'absolute',
    top: Platform.OS === 'web' ? 20 : 50,
    start: 20,
    end: 20,
    maxWidth: 450,
    alignSelf: 'center',
    borderRadius: tokens.radii.absolute["radius-12"],
    borderStartWidth: 4,
    zIndex: 9999,
    elevation: 10,
    // Platform-specific shadow
    ...(Platform.OS === 'web'
      ? {
          // @ts-ignore - boxShadow is valid for web
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.15,
          shadowRadius: 12,
        }),
  },
  content: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    padding: tokens.spacing.absolute["4"],
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginStart: tokens.spacing.absolute["3"],
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 15,
    fontFamily: FONTS.semiBold,
    color: tokens.colors.raw.gray["900"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["1"],
  },
  message: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  orgBadge: {
    alignSelf: 'flex-end',
    backgroundColor: tokens.colors.raw.red["50"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    borderRadius: tokens.radii.absolute["radius-4"],
    marginTop: tokens.spacing.absolute["2"],
  },
  orgText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
});

const styles = getStyles(true);
